import React, { useEffect, useRef, useState } from "react";
import { BuildingLibraryIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { useUser } from "../../contexts/UserContext";
import { useClient } from "../../contexts/ClientContext";

// Sidebar.jsx
// Working version: when the mouse is inside the sidebar and the user types,
// the search input will be focused AND the typed character will be inserted
// into the search box (so the first keystroke isn't lost).

export default function Sidebar({ open, setSidebarOpen }) {
  const { clients, selectedClientId, setSelectedClientId } = useClient();
  const { user } = useUser();

  const [search, setSearch] = useState("");
  const [sortOption, setSortOption] = useState("nameAsc");

  const searchInputRef = useRef(null);
  const navRef = useRef(null);

  // derived
  const searchTerm = String(search).toLowerCase();
  const filteredClients = (clients || []).filter((client) => {
    const valueToCheck = (client.short_code || client.customer_name || "").toLowerCase();
    return valueToCheck.includes(searchTerm);
  });

  const sortedClients = [...filteredClients].sort((a, b) => {
    switch (sortOption) {
      case "nameAsc":
        return (a.short_code || a.customer_name || "").localeCompare(
          b.short_code || b.customer_name || ""
        );
      case "nameDesc":
        return (b.short_code || b.customer_name || "").localeCompare(
          a.short_code || a.customer_name || ""
        );
      case "ticketAsc":
        return (a.ticket_count || 0) - (b.ticket_count || 0);
      case "ticketDesc":
        return (b.ticket_count || 0) - (a.ticket_count || 0);
      default:
        return 0;
    }
  });

  // Optional: auto-focus when the sidebar opens (and user is allowed)
  useEffect(() => {
    if (open && searchInputRef.current && Number(user?.role_id) !== 2) {
      // do not auto-insert any key here, just focus so keyboard users can start typing
      searchInputRef.current.focus();
    }
  }, [open, user]);

  useEffect(() => {
    // NOTE: keydown event does not deliver clientX/Y, so we track last mouse position
    const trackMouse = (e) => {
      window._lastMouseX = e.clientX;
      window._lastMouseY = e.clientY;
    };

    const isPrintableKey = (e) => {
      // Ignore modified keys (Ctrl/Alt/Meta) and non-character keys
      if (e.ctrlKey || e.altKey || e.metaKey) return false;
      // Backspace handled separately
      if (e.key === "Backspace") return true;
      // e.key length === 1 generally means a printable character (letters, digits, punctuation)
      return e.key.length === 1;
    };

    const handleKeyDown = (e) => {
      if (!navRef.current) return;
      // If sidebar isn't visible (closed) don't act
      const sidebarRect = navRef.current.getBoundingClientRect();
      if (sidebarRect.width === 0 && sidebarRect.height === 0) return;

      // compute mouse coords (keydown events often don't have clientX/Y)
      const mouseX = e.clientX ?? window._lastMouseX;
      const mouseY = e.clientY ?? window._lastMouseY;
      if (typeof mouseX !== "number" || typeof mouseY !== "number") return;

      // check if the last known mouse position is inside sidebar
      if (
        mouseX >= sidebarRect.left &&
        mouseX <= sidebarRect.right &&
        mouseY >= sidebarRect.top &&
        mouseY <= sidebarRect.bottom
      ) {
        if (!searchInputRef.current) return;

        // If input already focused, let browser handle the keystroke
        if (document.activeElement === searchInputRef.current) return;

        // Ignore navigation keys, function keys, etc.
        if (!isPrintableKey(e)) return;

        // Prevent default so other handlers don't steal the keystroke
        e.preventDefault();

        // Focus input and insert the keystroke into controlled state
        const key = e.key;

        if (key === "Backspace") {
          setSearch((prev) => {
            const next = prev.slice(0, -1);
            // Focus and set caret after state updates
            requestAnimationFrame(() => {
              const input = searchInputRef.current;
              if (input) {
                input.focus();
                input.setSelectionRange(next.length, next.length);
              }
            });
            return next;
          });
          return;
        }

        // Insert printable character
        setSearch((prev) => {
          const next = prev + key;

          requestAnimationFrame(() => {
            const input = searchInputRef.current;
            if (input) {
              input.focus();
              // move caret to end
              input.setSelectionRange(next.length, next.length);
            }
          });

          return next;
        });
      }
    };

    window.addEventListener("mousemove", trackMouse);
    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("mousemove", trackMouse);
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  return (
    <> 
      {Number(user?.role_id) !== 2 && Number(user?.role_id ?? user?.user_role_id) !== 16 && (
        <aside
          ref={navRef} // <-- IMPORTANT: attach the ref so effect can measure bounds
          className={`
            ${open ? "w-48 max-w-[60vw]" : "w-0"}
            bg-white shadow-md transition-all duration-300 ease-in-out
            flex flex-col fixed md:static z-40 h-full top-0 left-0 overflow-hidden
            border-r border-gray-200 rounded-tr-lg
          `}
          aria-hidden={!open}
        >
          <nav className="flex-1 px-2 py-4 space-y-4 text-gray-800">
            <div className="px-2 text-center">
              <span className="uppercase font-bold text-sm tracking-wide text-gray-700">
                {open ? "Client List" : "T"}
              </span>
            </div>

            <div className="pt-2 border-t border-gray-100 flex flex-col">
              {open && (
                <>
                  <input
                    ref={searchInputRef}
                    type="text"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Search client..."
                    className="w-full mb-2 px-3 py-1 border border-gray-200 rounded-md bg-white text-gray-700 placeholder-gray-400 text-sm focus:outline-none"
                    aria-label="Search clients"
                  />

                  <div className="flex gap-2 mb-2">
                    <button
                      onClick={() =>
                        setSortOption((prev) => (prev === "nameAsc" ? "nameDesc" : "nameAsc"))
                      }
                      className={`flex-1 px-2 py-1 text-xs border rounded-md transition
                        ${sortOption.includes("name") ? "bg-red-700 text-white" : "hover:bg-gray-100"}`}
                    >
                      {sortOption === "nameAsc" ? "A → Z" : "Z → A"}
                    </button>

                    <button
                      onClick={() =>
                        setSortOption((prev) => (prev === "ticketAsc" ? "ticketDesc" : "ticketAsc"))
                      }
                      className={`flex-1 px-2 py-1 text-xs border rounded-md transition
                        ${sortOption.includes("ticket") ? "bg-red-700 text-white" : "hover:bg-gray-100"}`}
                    >
                      {sortOption === "ticketAsc" ? "↑ Active" : "↓ Active"}
                    </button>
                  </div>
                </>
              )}

              <div className="mt-1 space-y-1 overflow-y-auto max-h-[calc(100vh-220px)] pr-1 sidebar-scroll">
                {sortedClients.map((client) => {
                  const isSelected = selectedClientId === client.cust_key;

                  return (
                    <button
                      key={client.cust_key}
                      onClick={() => {
                        setSelectedClientId((prev) => (prev === client.cust_key ? null : client.cust_key));

                        if (window.innerWidth <= 768 && typeof setSidebarOpen === "function") {
                          setSidebarOpen(false);
                        }
                      }}
                      className={`w-full flex items-center justify-between gap-2 py-2 px-3 rounded-lg transition-all duration-200 text-sm
                        ${isSelected ? "bg-red-50 border border-red-300 shadow-sm text-red-700 font-medium" : "hover:bg-gray-100 text-gray-700"}
                      `}
                    >
                      <div className="flex items-center gap-2 min-w-0">
                        <BuildingLibraryIcon className="w-5 h-5 text-gray-600 shrink-0" />
                        <span className="truncate">
                          {open ? (
                            <div className="flex items-center">
                              <span className="font-medium text-gray-900">{client.short_code || client.customer_name}</span>
                              {client.ticket_count > 0 && (
                                <span className="ml-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-red-600 text-white">{client.ticket_count}</span>
                              )}
                            </div>
                          ) : (
                            <div className="flex items-center">
                              <span className="font-medium text-gray-900">{(client.short_code || client.customer_name)?.charAt(0)?.toUpperCase()}</span>
                              {client.ticket_count > 0 && (
                                <span className="ml-2 text-xs font-semibold text-white bg-red-700 px-2 py-0.5 rounded-full">{client.ticket_count}</span>
                              )}
                            </div>
                          )}
                        </span>
                      </div>

                      {isSelected ? (
                        'x'
                      ) : (
                        !open && <span className="w-2 h-2 bg-gray-400 rounded-full shrink-0"></span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          </nav>
        </aside>
      )}
    </>
  );
}
